import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'wouter';
import { Icon } from '../components/icons/Icon';
import { TownHero } from '../components/town/TownHero';
import { Districts } from '../components/town/Districts';
import { CoCreationFlow } from '../components/town/CoCreationFlow';
import { PostList } from '../components/PostList';
import { SubmissionModal } from '../components/SubmissionModal';
import { GamenicsWidget } from '../components/GamenicsWidget';
import { GenesisRole } from '../components/RoleEntryButtons';
import { useSiteAssets } from '../hooks/useSiteAssets';
import { api, Post } from '../services/api';

/**
 * Home — 三役の街（モックC v2 実装）
 *
 * 構成: 街の門（ヒーロー）→ ゲームニクス（連続記録・直近目標3つ）→
 *       3街区（文壇サロン/トキワ荘/声劇ステージ）→ 共創のながれ（3起点合流）→
 *       街から生まれた作品（起点・完成作品一覧）
 *
 * - 画像スロット: Admin登録のロゴ/背景を useSiteAssets 経由で適用。
 *   未登録・読込失敗時は既定グラデーションで静かに動く（調停原則7）。
 * - 起点作品はどの役割からでも作成できる（SYSTEM_SPEC GEN-01: 3役対等）。
 */
/** 3役ボタン → 各町の対応（書く=文壇サロン / 描く=トキワ荘 / 演じる=声劇ステージ） */
const ROLE_TOWN_PATH: Record<GenesisRole, string> = {
    script: '/salon',
    illustration: '/tokiwa',
    voice: '/stage',
};

export const Home = () => {
    const [, setLocation] = useLocation();
    const [modalRole, setModalRole] = useState<GenesisRole | null>(null);
    const [posts, setPosts] = useState<Post[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const assets = useSiteAssets();

    const fetchPosts = useCallback(() => {
        setLoading(true);
        setError(false);
        api.getGenesisPosts()
            .then(setPosts)
            .catch(err => { console.error('Home: Fetch error:', err); setError(true); })
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { fetchPosts(); }, [fetchPosts]);

    return (
        <div className="w-full">
            {/* 3役ボタンは各町への入口（2026-08-14 オーナー決定）。起点投稿は街区カード・作品一覧に残す */}
            <TownHero heroBg={assets.heroBg} onSelectRole={(role) => setLocation(ROLE_TOWN_PATH[role])} />
            <GamenicsWidget />
            <Districts
                assets={assets}
                posts={posts}
                postsLoaded={!loading && !error}
                onCreateGenesis={setModalRole}
            />
            <CoCreationFlow />

            {/* 掲示板（BBS）への導線1箇所。ナビ本数は増やさず、街の説明の締めに置く（決定22・調停原則4） */}
            <div className="w-full max-w-[1120px] mx-auto px-6 my-10">
                <div className="pop-card p-6 text-center">
                    <h3 className="font-display font-black text-ink text-lg">まだ作らなくても、話せる。</h3>
                    <p className="mt-2 text-sm text-ink-2 max-w-xl mx-auto">
                        作品がなくても大丈夫。掲示板では、掲示板用の名前で気軽にひとこと交わせます（アカウント名は出ません）。
                    </p>
                    <button
                        className="btn btn-primary min-h-[44px] mt-4"
                        onClick={() => setLocation('/board')}
                    >
                        掲示板をのぞく <Icon name="arrow-right" size={16} />
                    </button>
                </div>
            </div>

            <PostList
                posts={posts}
                loading={loading}
                error={error}
                onRetry={fetchPosts}
                onCreateGenesis={setModalRole}
            />

            {modalRole && (
                <SubmissionModal
                    postId="genesis"
                    role={modalRole}
                    onClose={() => setModalRole(null)}
                    onSuccess={() => {
                        // 全リロードせず再フェッチ（音・スクロール位置・報酬演出を保つ）
                        setModalRole(null);
                        fetchPosts();
                    }}
                />
            )}
        </div>
    );
};
